import { axiosClient } from '@/lib/axiosClient';
import { buildApiBase } from '@/lib/buildApiBase';

// ============ API BASE CONSTANTS ============

const DISPUTE_API_BASE = `${buildApiBase()}/API/Dispute`;
const WORKFLOW_API_BASE = `${buildApiBase()}/API/Workflow`;

// ============ TYPES ============

export interface WorkflowState {
  code: string;
  name: string;
}

export interface WorkflowStateTransition {
  currentState: WorkflowState;
  lastModifiedDate: string | null;
}

export interface ArInvoice {
  invoiceAmount: number | null;
}

export interface DisputeSummarySource {
  text: string;
}

export interface DisputeSummaryItem {
  id: number;
  clientDisputeId: string | null;
  workflowStateTransition: WorkflowStateTransition;
  amount: number | null;
  writeOffAmount: number | null;
  vendorAmount: number | null;
  disputeSummarySource: DisputeSummarySource | null;
  curtailDate: string | null;
  createdDate: string | null;
  invoiceNumber: string | null;
  arInvoice: ArInvoice | null;
  dsLevel: string | null;
  createdBy: string | null;
  lastModifiedBy: string | null;
}

export interface FinancialWorkflowState {
  displayStatus: string;
}

export interface SourceSystem {
  id: number;
  text: string;
}

export interface DisputeDecision {
  id: number;
  text: string;
  value: string;
}

export interface BillingLineItem {
  punchCode: string | null;
  clientTotal: number | null;
  clientQuantity: number | null;
  clientSalesTaxAmount: number | null;
  vendorTotal: number | null;
}

export interface TaskType {
  name: string;
}

export interface Task {
  taskType: TaskType;
  billingLineItems: BillingLineItem[];
  vendorCode: string | null;
  chargebackOrderNumber: string | null;
}

export interface CategoryItem {
  id: number;
  name: string;
  cTermCode: string | null;
  editPermission: string | null;
}

export interface DisputeDetail {
  id: number;
  task: Task;
  writeOffAmount: string | null;
  writeOffGrid: number | null;
  vendorAmount: string | null;
  vendorCode: string | null;
  chargebackOrderNumber: string | null;
  disputeDetailDecisionId: number | null;
  disputeDetailDecision: DisputeDecision | null;
  decisionText: string | null;
  categoryId: string | number | null;
  categoryName: string | null;
  typeName: string | null;
  category: CategoryItem | string | null;
  type: CategoryItem | null;
  typeCategories: CategoryItem[];
  secondaryClassificationId: number | null;
  secondaryClassificationText: string | null;
  secondaryClassificationCategories: CategoryItem[];
  secondaryClassification: CategoryItem | null;
  other: string | null;
  salesTax: boolean;
  salesTaxWriteOff: string | null;
  clientTotalAmount: number | null;
  clientTotalQuantity: number | null;
  clientTotalSalesTaxAmount: number | null;
  previouslyDisputedAmount: number | null;
  previouslyDisputedTaxAmount: number | null;
  previouslyChargedBackAmount: number | null;
  paymentPeriods: number;
  appealComments: string | null;
  comments: string | null;
  expand: boolean;
  active: boolean;
  showAlert: boolean;
  isClientTotal: boolean;
  dsLevel: number;
  disputeSummaryId: number | null;
  groupId: string | null;
}

export interface AddDisputeSummaryData {
  id: number;
  groupId: string | null;
  invoiceNumber: string | null;
  arInvoice: { invoiceAmount: string | number | null } | null;
  amount: string | null;
  totalSalesTax: string;
  salesTaxWriteOff: string | null;
  curtailDate: string | null;
  clientDisputeId: string | null;
  comments: string | null;
  disputeSummarySourceId: number | null;
  disputeSummaryName: string | null;
  disputeDetails: DisputeDetail[];
  workflowStateTransition: WorkflowStateTransition;
  workflowStateTransitionId: number;
  writeOffAmount: string | null;
  vendorAmount: string | null;
  workOrderId?: string;
  orderNumber?: string;
  thresholdAmount?: number;
  dsLevel?: number;
}

// ============ API FUNCTIONS ============

export async function listDisputeSummary(workOrderId: string) {
  const response = await axiosClient.get<DisputeSummaryItem[]>(
    `${DISPUTE_API_BASE}/ListDisputeSummary`,
    { params: { workOrderId } }
  );
  return response.data;
}

export async function getInvoiceNumberByOrder(orderNumber: string) {
  const response = await axiosClient.get<string>(`${DISPUTE_API_BASE}/GetInvoiceNumberByOrder/`, {
    params: { orderNumber },
  });
  return response.data;
}

export async function getCurrentFinancialWorkflowState(orderNumber: string) {
  const response = await axiosClient.get<FinancialWorkflowState>(
    `${WORKFLOW_API_BASE}/GetCurrentWorkflowState`,
    { params: { orderNumber, flowName: 'WorkOrder Financials' } }
  );
  return response.data;
}

export async function getSourceSystems() {
  const response = await axiosClient.get<SourceSystem[]>(
    `${DISPUTE_API_BASE}/ListDisputeSummarySource`
  );
  return response.data;
}

export async function getDisputeDecisions() {
  const response = await axiosClient.get<DisputeDecision[]>(
    `${DISPUTE_API_BASE}/ListDisputeDetailDecision`
  );
  return response.data;
}

export async function getVendorCodes() {
  const response = await axiosClient.get<string[]>(`${DISPUTE_API_BASE}/GetVendorCodes`);
  return response.data;
}

export async function getCategories() {
  const response = await axiosClient.get<CategoryItem[]>(`${DISPUTE_API_BASE}/GetCategories`);
  return response.data;
}

export async function getCategoriesByParentId(parentCategoryId: number, moduleId: number) {
  const response = await axiosClient.get<CategoryItem[]>(
    `${DISPUTE_API_BASE}/GetCategoriesByParentId`,
    { params: { parentCategoryId, moduleId } }
  );
  return response.data;
}

export async function getDataToAddDisputeSummary(workOrderId: string, orderNumber: string) {
  const response = await axiosClient.get<AddDisputeSummaryData>(
    `${DISPUTE_API_BASE}/GetDataToAddDisputeSummary/`,
    { params: { workOrderId, orderNumber } }
  );
  return response.data;
}

export async function addDisputeSummary(disputeSummary: AddDisputeSummaryData) {
  const response = await axiosClient.post(`${DISPUTE_API_BASE}/AddDisputeSummary/`, disputeSummary);
  return response.data;
}

export async function getDisputeSummary(disputeSummaryId: number | string) {
  const response = await axiosClient.get<AddDisputeSummaryData>(
    `${DISPUTE_API_BASE}/GetDisputeSummary/`,
    { params: { disputeSummaryId } }
  );
  return response.data;
}

export async function updateDisputeSummary(disputeSummary: AddDisputeSummaryData) {
  const response = await axiosClient.post(
    `${DISPUTE_API_BASE}/UpdateDisputeSummary/`,
    disputeSummary
  );
  return response.data;
}

// ============ WORKFLOW HISTORY ============

export interface WorkflowHistoryItem {
  id: number;
  currentState: WorkflowState;
  workflowStateReason: { reason: string } | null;
  remarks: string | null;
  createdBy: string | null;
  toDate: string | null;
}

export async function getStateTransitionHistory(workflowStateTransitionId: number) {
  const response = await axiosClient.get<WorkflowHistoryItem[]>(
    `${WORKFLOW_API_BASE}/GetStateTransitionHistory`,
    { params: { WorkflowStateTransitionId: workflowStateTransitionId } }
  );
  return response.data;
}
